import React, { useMemo } from 'react'
import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import { useLocation } from 'react-router-dom'
import AiSparkIcon from '../../assets/icons/AiSparkIcon'
import { routeDomain } from './routeDomain'
import type { AgentDomain } from './surfaceRegistry'

type Capability = {
    domain: AgentDomain
    label: string
    /** Examples that act on what the user is already looking at. */
    onScreen: string[]
    /** Examples used from anywhere else — the assistant navigates first. */
    elsewhere: string[]
}

const CAPABILITIES: Capability[] = [
    {
        domain: 'chart',
        label: 'Charts',
        onScreen: [
            'Add RSI 14 and a 50-day moving average',
            'Switch this chart to 15-minute candles',
            'Split into four panes with SUNPHARMA, CIPLA, DRREDDY, and APOLLOHOSP',
        ],
        elsewhere: [
            'Open RELIANCE with RSI and volume',
            'Compare HDFC Bank and ICICI Bank side by side',
            'Show NIFTY on a daily chart with MACD',
        ],
    },
    {
        domain: 'screener',
        label: 'Screens',
        onScreen: [
            'Keep only large caps from this list',
            'Add PE ratio and sort by market cap',
            'Filter to stocks with RSI above 70',
        ],
        elsewhere: [
            'Stocks breaking out to new 52-week highs',
            'IT sector with PE below 20',
            'Large caps trading above their 200-day average',
        ],
    },
]

type Props = {
    onSelectPrompt: (text: string) => void
    disabled?: boolean
}

export default function AiAssistantEmptyState({ onSelectPrompt, disabled }: Props) {
    const location = useLocation()
    const activeDomain = routeDomain(location.pathname)

    // Lead with whatever the user is looking at, but always show both so the
    // rest of the assistant's reach stays discoverable.
    const groups = useMemo(
        () =>
            [...CAPABILITIES]
                .sort((a, b) => Number(b.domain === activeDomain) - Number(a.domain === activeDomain))
                .map((capability) => ({
                    label: capability.label,
                    prompts: capability.domain === activeDomain ? capability.onScreen : capability.elsewhere,
                })),
        [activeDomain],
    )

    return (
        <Box sx={{ py: 3 }}>
            <Stack alignItems="center" textAlign="center" sx={{ px: 1, mb: 3 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover',
                        color: 'text.secondary',
                        mb: 1.5,
                    }}
                >
                    <AiSparkIcon style={{ fontSize: 34 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    What are you looking for?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, lineHeight: 1.55 }}>
                    Explore stocks, build screens, and analyze charts
                </Typography>
            </Stack>

            <Stack spacing={2.5}>
                {groups.map((group) => (
                    <Box key={group.label}>
                        <Typography
                            variant="caption"
                            color="text.tertiary"
                            sx={{
                                display: 'block',
                                mb: 1,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                fontWeight: 600,
                            }}
                        >
                            {group.label}
                        </Typography>
                        <Stack spacing={0.75}>
                            {group.prompts.map((prompt) => (
                                <ButtonBase
                                    key={prompt}
                                    disabled={disabled}
                                    onClick={() => onSelectPrompt(prompt)}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        textAlign: 'left',
                                        width: '100%',
                                        px: 1.25,
                                        py: 0.875,
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        color: 'text.secondary',
                                        transition: 'border-color 0.15s, background-color 0.15s',
                                        '&:hover': {
                                            borderColor: 'text.disabled',
                                            bgcolor: 'action.hover',
                                            color: 'text.primary',
                                        },
                                        '&.Mui-disabled': { opacity: 0.5 },
                                    }}
                                >
                                    <Typography variant="body2" sx={{ minWidth: 0 }}>
                                        {prompt}
                                    </Typography>
                                </ButtonBase>
                            ))}
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </Box>
    )
}
